// The right rail while a CHAT is on screen (busy-my-web#450; chat Figma rebuild WP5, frames 03, 04,
// 08, 18, 26, 33, 34 — section 40011407:276544, timeline items 40012330:369644).
//
// The rail is the list of CONVERSATIONS: one row per session, oldest at the top and the live one
// last, the way a timeline reads downward into "now". Each row is the chat glyph in the rail's 24px
// disc, the conversation's name, one line saying what it amounted to ("8 questions · until 6:30 pm",
// "Conversation started", "Conversation edited", "Web research"), the stacked "Sep 14 / 6 PM" stamp,
// and — under it — what the conversation DID: "✓ Task created · Buy a new iPhone", "Connected to
// Dad's birthday". The rail ends with "+ New conversation", or, once one has been started and
// nothing asked in it yet, with that new conversation's own row ("Ready when you are", frame 34).
//
// Not a row per turn: the turns ARE the middle of the page, and a second copy of them down the rail
// would be the same conversation said twice.
//
// Pure, like every other rail builder (lib/emailSession's `sessionItem`, lib/entityTimeline): given
// the sessions and the turns on screen, what should the rail say. The store (chatSessions) keeps the
// sessions; components/SidePanel/ChatTimeline.vue only draws what this returns.

import { parseUtc } from '@/lib/dueStatus'
import { clockTime } from '@/lib/timelineTime'

/** The rail's own header, in the frames' word. */
export const CHAT_RAIL_TITLE = 'Timeline'

export const CHAT_RAIL_TEXT = {
  started: 'Conversation started',
  edited: 'Conversation edited',
  webResearch: 'Web research',
  ready: 'Ready when you are',
  newConversation: 'New conversation',
}

/** Id of the live row when the live session has no server id (a guest, or before the first ask). */
export const LIVE_ROW_ID = 'live'

const clean = (v) => String(v ?? '').replace(/\s+/g, ' ').trim()
const list = (v) => (Array.isArray(v) ? v.filter(Boolean) : [])
const time = (v) => {
  if (v === null || v === undefined || v === '') return NaN
  const t = parseUtc(v).getTime()
  return Number.isNaN(t) ? NaN : t
}
const latest = (...values) => {
  const known = values.filter((v) => !Number.isNaN(time(v)))
  return known.length ? known.reduce((a, b) => (time(b) > time(a) ? b : a)) : null
}

/**
 * The one row for the conversation on screen, or null when there is none to describe.
 *
 * Kept for the panel (components/SidePanel/index.vue), which asks it a yes/no question: is there a
 * chat on screen? `title` is the question the chat OPENED with; "Conversation edited" once any
 * question in it has been rewritten.
 *
 * @param {Array<{question?:string, askedUtc?:string, edited?:boolean}>} conversation
 * @returns {{title:string, description:string, at:string|null}|null}
 */
export function chatRailItem(conversation) {
  const turns = list(conversation)
  if (!turns.length) return null
  const first = turns[0]
  const title = clean(first.question)
  if (!title) return null
  const edited = turns.some(t => t?.edited)
  return {
    title,
    description: edited ? CHAT_RAIL_TEXT.edited : CHAT_RAIL_TEXT.started,
    at: first.askedUtc || null,
  }
}

/**
 * The stacked stamp on the right of a row: "Sep 14" over "6 PM" (frame 03; GAPS C3).
 *
 * Month first and an upper-case meridiem — the chat rail's own shape, which is not the day feed's
 * "14 Sep / 6:00 pm" (lib/timelineTime) — and a whole hour drops its ":00", as every row in the
 * frames does ("6 PM", "12:37 PM", "7:30 PM"). Built by hand so every locale draws the same thing.
 */
export function railStamp(value) {
  const t = time(value)
  if (Number.isNaN(t)) return { date: '', time: '' }
  const dt = new Date(t)
  const month = dt.toLocaleDateString('en-US', { month: 'short' })
  const hours = dt.getHours()
  const minutes = dt.getMinutes()
  const clock = `${hours % 12 || 12}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''} ${hours >= 12 ? 'PM' : 'AM'}`
  return { date: `${month} ${dt.getDate()}`, time: clock }
}

/**
 * "8 questions · until 6:30 pm" — how much was asked, and when the conversation last moved.
 * One question is "1 question"; with no time the "until" half is left off.
 */
export function questionsLabel(count, untilUtc) {
  const n = Math.max(0, Number(count) || 0)
  const head = `${n} ${n === 1 ? 'question' : 'questions'}`
  const until = clockTime(untilUtc)
  return until ? `${head} · until ${until}` : head
}

// ── Child lines: what a conversation DID ─────────────────────────────────────────────────────────

const capitalise = (s) => s.charAt(0).toUpperCase() + s.slice(1)

/**
 * One event as the child line under a row, or null for a kind the rail does not draw.
 *
 *   task_created (and note_created, event_created…) → "Task created · Buy a new iPhone", green tick
 *   connected                                       → "Connected to Dad's birthday", link glyph
 *
 * `web_research` is not a line: it is what the row's SUBTITLE says (`subtitleOf`).
 */
export function childLine(event) {
  const kind = clean(event?.kind).toLowerCase()
  const title = clean(event?.title)
  const created = /^([a-z]+)_created$/.exec(kind)
  if (created) {
    const text = `${capitalise(created[1])} created`
    return { kind: 'created', icon: 'tick-circle', text: title ? `${text} · ${title}` : text }
  }
  if (kind === 'connected') {
    return { kind: 'connected', icon: 'link', text: title ? `Connected to ${title}` : 'Connected' }
  }
  return null
}

/** Every drawable event as a child line, each fact once (the same task created twice is one line). */
export function childLines(events) {
  const seen = new Set()
  const out = []
  for (const event of list(events)) {
    const line = childLine(event)
    if (!line) continue
    const id = `${line.kind}:${event.refId || line.text}`
    if (seen.has(id)) continue
    seen.add(id)
    out.push(line)
  }
  return out
}

// WP4 marks a row the user connected; either spelling is read so the line does not depend on which.
const connected = (row) => row.connected === true || clean(row.state || row.status).toLowerCase() === 'connected'

/**
 * The events a LIVE conversation has produced so far, read off its answers' blocks (PLAN §2): a
 * task the chat created (`entityCreated`, including a suggestion that was created in place) and a
 * connection the user made from a `connections` table. The server derives the same list for stored
 * sessions (`ChatSessionDto.events`); this is the half that cannot wait for a reload.
 */
export function eventsFromTurns(turns) {
  const events = []
  for (const turn of list(turns)) {
    for (const block of list(turn.blocks)) {
      if (block.type === 'entityCreated') {
        events.push({ kind: `${clean(block.kind) || 'task'}_created`, title: block.title, refId: block.id || null })
      } else if (block.type === 'connections') {
        for (const row of list(block.rows).filter(connected)) {
          // …named after the Ongoing it joined when the block recorded one (frame 21: "Connected to
          // Dad's birthday"), else after the row.
          events.push({ kind: 'connected', title: clean(row.connectedTo) || row.title, refId: row.ref?.id || row.id || null })
        }
      }
    }
  }
  return events
}

// ── Sessions ─────────────────────────────────────────────────────────────────────────────────────

/**
 * The turns that belong to the LIVE conversation. "+ New conversation" marks the turns on screen
 * `archived` (they stay drawn above the "New" divider, frame 18); everything else is live.
 */
export function liveTurns(turns) {
  return list(turns).filter((t) => !t.archived)
}

const asked = (turns) => list(turns).filter((t) => clean(t.question))
// A question counts once it has an answer on screen: one still being thought about (frame 18) or
// stopped before anything came back has not been answered.
const answered = (turns) => asked(turns).filter((t) => !t.asking && (clean(t.answer) || list(t.blocks).length))

/**
 * A stored session's summary (`ChatSessionDto`, WP6 fields optional), normalised.
 *
 * `questionCount` stays null when the server did not count — "not counted" is not zero, and the row
 * then says "Conversation started" rather than "0 questions". A session that came with its messages
 * is counted here instead.
 */
export function sessionSummary(dto) {
  if (!dto || !dto.id) return null
  const messages = Array.isArray(dto.messages) ? dto.messages : null
  const users = messages ? messages.filter((m) => String(m?.role || '').toLowerCase() === 'user' && clean(m.text)) : []
  const count = Number.isFinite(dto.questionCount) ? dto.questionCount : (messages ? users.length : null)
  const lastMessage = messages && messages.length ? messages[messages.length - 1]?.createdUtc : null
  return {
    id: String(dto.id),
    title: clean(dto.title) || clean(users[0]?.text),
    createdUtc: dto.createdUtc || null,
    lastMessageUtc: dto.lastMessageUtc || lastMessage || dto.updatedUtc || null,
    questionCount: count,
    events: list(dto.events),
    edited: !!dto.edited,
    webResearch: !!dto.webResearch || list(dto.events).some((e) => clean(e.kind).toLowerCase() === 'web_research'),
  }
}

/**
 * A summary of a conversation from the turns on screen (+ what the server already knew of it), for
 * the live row and for the session "+ New conversation" just archived.
 */
export function summaryFromTurns(turns, { id = null, base = null, research = null } = {}) {
  const live = list(turns)
  const done = answered(live)
  const baseCount = Number(base?.questionCount) || 0
  const first = asked(live)[0]
  const last = done[done.length - 1]
  const lastLocal = last ? (last.answeredUtc || last.askedUtc) : null
  const researchQuestion = clean(research?.question)
  const title = (baseCount && clean(base?.title)) || clean(first?.question) || clean(base?.title) || researchQuestion
  if (!title) return null
  return {
    id: id || base?.id || LIVE_ROW_ID,
    title,
    createdUtc: (baseCount && base?.createdUtc) || first?.askedUtc || research?.at || base?.createdUtc || null,
    lastMessageUtc: latest(lastLocal, base?.lastMessageUtc) || null,
    questionCount: baseCount + done.length,
    events: [...list(base?.events), ...eventsFromTurns(live)],
    edited: live.some((t) => t.edited),
    // The latest thing this conversation did was a web research (the question went to Research
    // mode — store/modules/timelineSearch `routeToResearch`).
    webResearch: !!researchQuestion && !(time(lastLocal) > time(research?.at)),
  }
}

/** What a row says under its name. Edited and web research outrank the count, as in 26 and 33. */
export function subtitleOf(summary) {
  if (summary.edited) return CHAT_RAIL_TEXT.edited
  if (summary.webResearch) return CHAT_RAIL_TEXT.webResearch
  if (summary.questionCount) return questionsLabel(summary.questionCount, summary.lastMessageUtc)
  return CHAT_RAIL_TEXT.started
}

const rowOf = (summary, extra = {}) => ({
  id: summary.id,
  title: summary.title,
  subtitle: subtitleOf(summary),
  stamp: railStamp(summary.createdUtc || summary.lastMessageUtc),
  children: childLines(summary.events),
  active: false,
  live: false,
  placeholder: false,
  ...extra,
})

// Ordered by when a conversation STARTED — the time its stamp shows — so the stamps read in order
// down the rail (frame 03: "Sep 3 8 am" sits above "Sep 3 9 AM" whatever was said in it later).
const sortKey = (s) => {
  const t = time(s.createdUtc || s.lastMessageUtc)
  return Number.isNaN(t) ? -Infinity : t
}

/**
 * Every row of the rail, top to bottom, and whether it ends with "+ New conversation".
 *
 * @param {object} input
 * @param {Array}  input.turns        the turns on screen (timelineSearch.conversation)
 * @param {Array}  [input.stash]      the live turns, while a PAST session is shown in the middle
 * @param {string} [input.viewingId]  the past session shown in the middle (its row is the dark one)
 * @param {boolean}[input.pendingBreak] "+ New conversation" was pressed and nothing asked since
 * @param {string} [input.pendingNewUtc] when it was pressed — the "Ready when you are" row's stamp
 * @param {Array}  [input.recent]     stored sessions (ChatSessionDto), any order
 * @param {object} [input.live]       the live session as the server last described it
 * @param {string} [input.liveId]     the live session's id (timelineSearch.sessionId)
 * @param {object} [input.research]   { question, at } — the live conversation's web research
 * @returns {{ rows: Array, showNew: boolean }}
 */
export function chatRailRows({
  turns = [], stash = null, viewingId = null, pendingBreak = false, pendingNewUtc = null,
  recent = [], live = null, liveId = null, research = null,
} = {}) {
  const liveKey = liveId ? String(liveId) : null
  const past = list(recent)
    .map(sessionSummary)
    .filter((s) => s && s.id !== liveKey && s.id !== LIVE_ROW_ID)
    .sort((a, b) => sortKey(a) - sortKey(b))
  const rows = past.map((s) => rowOf(s, { active: !!viewingId && s.id === String(viewingId) }))

  const onScreen = viewingId ? list(stash) : list(turns)
  const base = live && (!liveKey || String(live.id) === liveKey) ? sessionSummary(live) : null
  const summary = summaryFromTurns(liveTurns(onScreen), { id: liveKey, base, research })
  if (summary) {
    rows.push(rowOf(summary, { live: true }))
    return { rows, showNew: true }
  }
  // "+ New conversation" was pressed and nothing has been asked since (frame 34): the new
  // conversation's own row stands where the button was.
  if (pendingBreak) {
    rows.push({
      id: 'pending', title: CHAT_RAIL_TEXT.ready, subtitle: CHAT_RAIL_TEXT.newConversation,
      stamp: railStamp(pendingNewUtc), children: [], active: false, live: true, placeholder: true,
    })
    return { rows, showNew: false }
  }
  return { rows, showNew: rows.length > 0 }
}

export default chatRailItem
